import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoAdd, IoChevronDown, IoEllipsisHorizontal } from 'react-icons/io5';
import YearStructurePeriodEditor from '../YearStructurePeriodEditor';
import MakePeriodFromDraftModal from './MakePeriodFromDraftModal';
import LiveBackupManager from '../../services/LiveBackupManager.js';
import {
    formatPeriodBoundsLabel,
    formatPeriodShortMonthYearRange
} from '../../utils/yearStructurePeriodUtils';

const getDraftPeriodId = (draft) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getDraftId = (draft) => {
    const raw = draft?.id;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getPeriodActiveDraftId = (period) => {
    const raw = period?.active_schedule_draft_id ?? period?.activeScheduleDraftId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const draftMatchesPeriod = (draft, periodId) => {
    if (periodId === '__unassigned__') {
        return getDraftPeriodId(draft) === null;
    }

    const draftPeriodId = getDraftPeriodId(draft);
    if (!draftPeriodId) return false;
    return String(draftPeriodId) === String(periodId);
};

const generateCopyName = (draftName, drafts) => {
    const existingNames = new Set((drafts || []).map((draft) => draft?.draft_name).filter(Boolean));
    const baseName = `${draftName} Copy`;
    if (!existingNames.has(baseName)) {
        return baseName;
    }

    let suffix = 2;
    while (existingNames.has(`${baseName} ${suffix}`)) {
        suffix += 1;
    }
    return `${baseName} ${suffix}`;
};

const getPeriodCalendarYear = (period) => {
    if (!period) return null;
    return period.year ?? period.start_week_year ?? period.startWeekYear ?? null;
};

const generateNewDraftName = (period, drafts) => {
    const baseName = period?.name ? `${period.name} Version` : 'New Version';
    const existingNames = new Set((drafts || []).map((draft) => draft?.draft_name).filter(Boolean));
    if (!existingNames.has(baseName)) {
        return baseName;
    }

    let suffix = 2;
    while (existingNames.has(`${baseName} ${suffix}`)) {
        suffix += 1;
    }
    return `${baseName} ${suffix}`;
};

const DraftPeriodSidebar = ({
    currentDraftName,
    draftManager,
    onModeChange,
    onDraftChange,
    onToggleDraftLive,
    onCurrentDraftDeleted,
    refreshTimePeriods,
    refreshScheduleEntries,
    timePeriods = [],
    selectedPeriodId,
    selectedYearForView,
    setError,
    setSuccess,
    setIsLoading
}) => {
    const [drafts, setDrafts] = useState([]);
    const [loadingDrafts, setLoadingDrafts] = useState(false);
    const [openMenuDraftName, setOpenMenuDraftName] = useState(null);
    const [showPeriodEditor, setShowPeriodEditor] = useState(false);
    const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(false);
    const [showDraftCreator, setShowDraftCreator] = useState(false);
    const [newDraftName, setNewDraftName] = useState('');
    const [creatingDraft, setCreatingDraft] = useState(false);
    const [makePeriodDraft, setMakePeriodDraft] = useState(null);
    const newDraftNameInputRef = useRef(null);
    const periodMenuRef = useRef(null);
    const [filterPeriodId, setFilterPeriodId] = useState(() => {
        if (selectedPeriodId != null && selectedPeriodId !== '') {
            return String(selectedPeriodId);
        }
        return timePeriods[0]?.id != null ? String(timePeriods[0].id) : '';
    });

    const loadDrafts = useCallback(async () => {
        if (!draftManager) return;
        setLoadingDrafts(true);
        try {
            const draftList = await draftManager.fetchDrafts();
            const visibleDrafts = (draftList || []).filter(
                (draft) => !(draft?.draft_name && LiveBackupManager.isBackupName(String(draft.draft_name)))
            );
            setDrafts(visibleDrafts);
        } catch (err) {
            console.error('Error loading drafts:', err);
        } finally {
            setLoadingDrafts(false);
        }
    }, [draftManager]);

    useEffect(() => {
        loadDrafts();
    }, [loadDrafts]);

    useEffect(() => {
        if (!draftManager) return undefined;

        const handleDraftManagerChange = (state) => {
            if (state.drafts) {
                const visibleDrafts = (state.drafts || []).filter(
                    (draft) => !(draft?.draft_name && LiveBackupManager.isBackupName(String(draft.draft_name)))
                );
                setDrafts(visibleDrafts);
            }
        };

        draftManager.addListener(handleDraftManagerChange);
        return () => draftManager.removeListener(handleDraftManagerChange);
    }, [draftManager]);

    useEffect(() => {
        if (!currentDraftName) {
            if (selectedPeriodId != null && selectedPeriodId !== '') {
                setFilterPeriodId(String(selectedPeriodId));
            }
            return;
        }

        const currentDraft = drafts.find((draft) => draft.draft_name === currentDraftName);
        const draftPeriodId = getDraftPeriodId(currentDraft);
        if (draftPeriodId != null) {
            setFilterPeriodId(String(draftPeriodId));
            return;
        }

        if (selectedPeriodId != null && selectedPeriodId !== '') {
            setFilterPeriodId(String(selectedPeriodId));
        }
    }, [currentDraftName, drafts, selectedPeriodId]);

    const hasUnscopedDrafts = useMemo(
        () => drafts.some((draft) => getDraftPeriodId(draft) === null),
        [drafts]
    );

    const periodOptions = useMemo(() => {
        const options = (timePeriods || []).map((period) => ({
            id: String(period.id),
            name: period.name || 'Period',
            timeframe: formatPeriodShortMonthYearRange(period),
            bounds: formatPeriodBoundsLabel(period),
            color: period.color || '#3B82F6',
            period
        }));

        if (hasUnscopedDrafts) {
            options.push({
                id: '__unassigned__',
                name: 'Unscoped drafts',
                timeframe: '',
                bounds: '',
                color: '#9CA3AF',
                period: null
            });
        }

        return options;
    }, [timePeriods, hasUnscopedDrafts]);

    const selectedPeriod = useMemo(() => {
        if (filterPeriodId === '__unassigned__') return null;
        return (timePeriods || []).find((period) => String(period.id) === String(filterPeriodId)) || null;
    }, [filterPeriodId, timePeriods]);

    const selectedPeriodTitle = useMemo(() => {
        if (filterPeriodId === '__unassigned__') return 'Unscoped drafts';
        return selectedPeriod?.name || (periodOptions.length === 0 ? 'No periods' : 'Select period');
    }, [filterPeriodId, selectedPeriod, periodOptions.length]);

    const selectedPeriodTimeframe = useMemo(() => {
        if (!selectedPeriod) return '';
        return formatPeriodShortMonthYearRange(selectedPeriod);
    }, [selectedPeriod]);

    const selectedPeriodBounds = useMemo(() => {
        if (!selectedPeriod) return '';
        return formatPeriodBoundsLabel(selectedPeriod);
    }, [selectedPeriod]);

    const selectedPeriodColor = selectedPeriod?.color || '#3B82F6';

    useEffect(() => {
        if (!isPeriodMenuOpen) return undefined;

        const handlePointerDown = (event) => {
            if (periodMenuRef.current && !periodMenuRef.current.contains(event.target)) {
                setIsPeriodMenuOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsPeriodMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isPeriodMenuOpen]);

    const filteredDrafts = useMemo(() => {
        if (!filterPeriodId) return [];
        return drafts.filter((draft) => draftMatchesPeriod(draft, filterPeriodId));
    }, [drafts, filterPeriodId]);

    const activeDraftIdForFilterPeriod = useMemo(
        () => getPeriodActiveDraftId(selectedPeriod),
        [selectedPeriod]
    );

    const isDraftActiveForPeriod = useCallback((draft) => {
        const draftId = getDraftId(draft);
        if (!draftId || !activeDraftIdForFilterPeriod) return false;
        return String(draftId) === String(activeDraftIdForFilterPeriod);
    }, [activeDraftIdForFilterPeriod]);

    const handleLoadDraft = async (draftName) => {
        if (!draftName || draftName === currentDraftName) return;

        if (setIsLoading) setIsLoading(true);
        try {
            if (draftManager) {
                draftManager.setDraftMode(draftName);
            }
            onModeChange(false, draftName);

            if (setSuccess) {
                setSuccess(`Loaded draft "${draftName}"`);
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            if (setError) setError('Failed to load draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleToggleLiveDraft = async (draft, isActive) => {
        if (typeof onToggleDraftLive !== 'function') {
            if (setError) setError('Cannot update live draft: live draft handler is unavailable.');
            return;
        }

        try {
            const result = await onToggleDraftLive(draft, isActive, {
                onAfterSuccess: () => setOpenMenuDraftName(null)
            });
            if (result === true) setOpenMenuDraftName(null);
        } catch (err) {
            if (setError) setError(`Failed to ${isActive ? 'deactivate' : 'activate'} draft: ${err.message}`);
        }
    };

    const handleDuplicateDraft = async (draft) => {
        if (!draftManager || !draft?.draft_name) return;

        const defaultName = generateCopyName(draft.draft_name, drafts);
        const newName = window.prompt('Duplicate draft as:', defaultName);
        if (!newName || !newName.trim()) return;

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.duplicateDraft(draft.draft_name, newName.trim());
            if (setSuccess) {
                setSuccess(`Draft "${draft.draft_name}" duplicated as "${newName.trim()}".`);
                setTimeout(() => setSuccess(null), 3000);
            }
            setOpenMenuDraftName(null);
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to duplicate draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleRenameDraft = async (draft) => {
        if (!draftManager || !draft?.draft_name) return;

        const newName = window.prompt('Rename draft:', draft.draft_name);
        if (!newName || !newName.trim() || newName.trim() === draft.draft_name) return;

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.renameDraft(draft.draft_name, newName.trim());
            if (currentDraftName === draft.draft_name && onDraftChange) {
                onDraftChange(newName.trim());
            }
            if (setSuccess) {
                setSuccess(`Draft renamed to "${newName.trim()}".`);
                setTimeout(() => setSuccess(null), 3000);
            }
            setOpenMenuDraftName(null);
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to rename draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleDeleteDraft = async (draft, isActive) => {
        if (!draftManager || !draft?.draft_name) return;

        const confirmed = window.confirm(`Delete draft "${draft.draft_name}"? This cannot be undone.`);
        if (!confirmed) return;

        if (setIsLoading) setIsLoading(true);
        try {
            if (isActive) {
                if (typeof onToggleDraftLive !== 'function') {
                    if (setError) setError('Cannot delete live draft: live draft handler is unavailable.');
                    return;
                }
                const deactivated = await onToggleDraftLive(draft, true, {
                    savePendingFirst: false,
                    skipConfirm: true
                });
                if (!deactivated) {
                    return;
                }
            }
            await draftManager.deleteDraft(draft.draft_name);
            if (currentDraftName === draft.draft_name) {
                if (typeof onCurrentDraftDeleted === 'function') {
                    await onCurrentDraftDeleted();
                }
            }
            if (setSuccess) {
                setSuccess(`Draft "${draft.draft_name}" deleted.`);
                setTimeout(() => setSuccess(null), 3000);
            }
            setOpenMenuDraftName(null);
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to delete draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const currentDraftInFilter = filteredDrafts.some((draft) => draft.draft_name === currentDraftName);

    const handlePeriodCreated = async ({ period }) => {
        if (refreshTimePeriods) {
            await refreshTimePeriods();
        }
        if (period?.id != null) {
            setFilterPeriodId(String(period.id));
        }
        setShowPeriodEditor(false);
        setIsPeriodMenuOpen(false);
        if (setSuccess) {
            const label = period?.name ? `"${period.name}"` : 'Period';
            setSuccess(`${label} added successfully.`);
            setTimeout(() => setSuccess(null), 3000);
        }
    };

    const handlePeriodCreatedFromDraft = async ({ period }) => {
        const attachedDraftName = makePeriodDraft?.draft_name;
        if (refreshTimePeriods) {
            await refreshTimePeriods();
        }
        if (period?.id != null) {
            setFilterPeriodId(String(period.id));
        }
        setIsPeriodMenuOpen(false);
        setMakePeriodDraft(null);
        await loadDrafts();
        if (typeof refreshScheduleEntries === 'function') {
            try {
                await refreshScheduleEntries();
            } catch (_) {
                // Period and draft association already succeeded.
            }
        }
        if (setSuccess) {
            const periodLabel = period?.name ? `"${period.name}"` : 'Period';
            setSuccess(attachedDraftName
                ? `${periodLabel} created and "${attachedDraftName}" attached. It is not live yet.`
                : `${periodLabel} added successfully.`);
            setTimeout(() => setSuccess(null), 4000);
        }
    };

    const makePeriodLivePeriodName = useMemo(() => {
        if (!makePeriodDraft) return '';
        const draftId = getDraftId(makePeriodDraft);
        if (!draftId) return '';
        const livePeriod = (timePeriods || []).find((period) => {
            const activeDraftId = getPeriodActiveDraftId(period);
            return activeDraftId && String(activeDraftId) === String(draftId);
        });
        return livePeriod?.name || '';
    }, [makePeriodDraft, timePeriods]);

    const makePeriodSourcePeriod = useMemo(() => {
        const periodId = getDraftPeriodId(makePeriodDraft);
        if (!periodId) return null;
        return (timePeriods || []).find((period) => String(period.id) === String(periodId))
            || makePeriodDraft?.period
            || null;
    }, [makePeriodDraft, timePeriods]);

    const openMakePeriodDialog = (draft) => {
        setOpenMenuDraftName(null);
        setMakePeriodDraft(draft);
    };

    const openDraftCreator = () => {
        if (showDraftCreator) {
            setShowDraftCreator(false);
            setNewDraftName('');
            return;
        }

        setNewDraftName(generateNewDraftName(selectedPeriod, drafts));
        setShowDraftCreator(true);
    };

    useEffect(() => {
        if (showDraftCreator) {
            newDraftNameInputRef.current?.focus();
            newDraftNameInputRef.current?.select();
        }
    }, [showDraftCreator]);

    const handleCreateDraft = async () => {
        if (!newDraftName.trim()) {
            if (setError) setError('Please enter a version name');
            return;
        }

        if (!draftManager) {
            if (setError) setError('Draft manager not available');
            return;
        }

        if (!filterPeriodId) {
            if (setError) setError('Select a period before creating a version');
            return;
        }

        const periodId = filterPeriodId === '__unassigned__' ? null : filterPeriodId;
        const draftYear = getPeriodCalendarYear(selectedPeriod) ?? selectedYearForView ?? new Date().getFullYear();

        setCreatingDraft(true);
        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.createEmptyDraft(newDraftName.trim(), draftYear, periodId);
            onModeChange(false, newDraftName.trim());
            setShowDraftCreator(false);
            setNewDraftName('');
            if (setSuccess) {
                setSuccess(`Version "${newDraftName.trim()}" created.`);
                setTimeout(() => setSuccess(null), 3000);
            }
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to create version: ' + err.message);
        } finally {
            setCreatingDraft(false);
            if (setIsLoading) setIsLoading(false);
        }
    };

    return (
        <aside className="flex-none w-80 py-4 px-2">
            <div className={`h-full rounded-2xl border border-gray-200 bg-white shadow-sm p-3 ${isPeriodMenuOpen ? 'overflow-visible' : 'overflow-y-auto'}`}>
                <div className="mb-2 flex items-center justify-end">
                    <button
                        type="button"
                        onClick={() => {
                            setShowPeriodEditor((open) => !open);
                            setIsPeriodMenuOpen(false);
                        }}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        aria-label={showPeriodEditor ? 'Close period editor' : 'Add schedule period'}
                        title={showPeriodEditor ? 'Close' : 'Add period'}
                    >
                        <IoAdd className={`h-5 w-5 transition-transform ${showPeriodEditor ? 'rotate-45' : ''}`} />
                    </button>
                </div>

                {showPeriodEditor && (
                    <YearStructurePeriodEditor
                        variant="compact"
                        className="mb-3"
                        setError={setError}
                        onSuccess={handlePeriodCreated}
                        onCancel={() => setShowPeriodEditor(false)}
                    />
                )}

                <div className={`rounded-2xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white ${isPeriodMenuOpen ? 'overflow-visible' : 'overflow-hidden'}`}>
                    <div
                        ref={periodMenuRef}
                        className={`relative border-b border-gray-200/80 ${isPeriodMenuOpen ? 'z-50' : ''}`}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                if (periodOptions.length === 0) return;
                                setIsPeriodMenuOpen((open) => !open);
                            }}
                            disabled={periodOptions.length === 0}
                            className="group flex w-full items-start gap-3 px-3.5 py-3.5 text-left transition-colors hover:bg-white/70 disabled:cursor-not-allowed disabled:opacity-60"
                            aria-haspopup="listbox"
                            aria-expanded={isPeriodMenuOpen}
                        >
                            <span
                                className="mt-1.5 h-3 w-3 flex-none rounded-full ring-2 ring-white shadow-sm"
                                style={{ backgroundColor: selectedPeriodColor }}
                                aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-400">
                                    Schedule period
                                </p>
                                <div className="mt-0.5 flex items-center gap-1.5">
                                    <h2 className="truncate text-lg font-semibold leading-tight text-gray-900">
                                        {selectedPeriodTitle}
                                    </h2>
                                    <IoChevronDown
                                        className={`h-4 w-4 flex-none text-gray-400 transition-transform group-hover:text-gray-600 ${isPeriodMenuOpen ? 'rotate-180' : ''}`}
                                        aria-hidden="true"
                                    />
                                </div>
                                {selectedPeriodTimeframe ? (
                                    <p className="mt-1 text-sm text-gray-500">
                                        {selectedPeriodTimeframe}
                                        {selectedPeriodBounds ? (
                                            <span className="text-gray-400"> · {selectedPeriodBounds}</span>
                                        ) : null}
                                    </p>
                                ) : filterPeriodId === '__unassigned__' ? (
                                    <p className="mt-1 text-sm text-gray-500">Not attached to a period</p>
                                ) : null}
                            </div>
                        </button>

                        {isPeriodMenuOpen && (
                            <div
                                role="listbox"
                                className="absolute left-2 right-2 top-[calc(100%-0.25rem)] z-50 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                            >
                                {periodOptions.map((option) => {
                                    const isSelectedOption = String(option.id) === String(filterPeriodId);
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            role="option"
                                            aria-selected={isSelectedOption}
                                            onClick={() => {
                                                setFilterPeriodId(option.id);
                                                setIsPeriodMenuOpen(false);
                                            }}
                                            className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors ${isSelectedOption ? 'bg-blue-50' : 'hover:bg-gray-50'
                                                }`}
                                        >
                                            <span
                                                className="mt-1 h-2.5 w-2.5 flex-none rounded-full"
                                                style={{ backgroundColor: option.color }}
                                                aria-hidden="true"
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className={`block truncate text-sm ${isSelectedOption ? 'font-semibold text-blue-900' : 'font-medium text-gray-900'}`}>
                                                    {option.name}
                                                </span>
                                                {option.timeframe ? (
                                                    <span className="mt-0.5 block text-xs text-gray-500">
                                                        {option.timeframe}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="px-3 pb-3 pt-2.5">
                        <div className="mb-2 flex items-center justify-between pl-1">
                            <div className="flex items-center gap-2">
                                <span className="h-4 w-px bg-gray-300" aria-hidden="true" />
                                <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-500">
                                    Versions
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={openDraftCreator}
                                disabled={!filterPeriodId || creatingDraft}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                                aria-label={showDraftCreator ? 'Close version creator' : 'Add version'}
                                title={showDraftCreator ? 'Close' : 'Add version'}
                            >
                                <IoAdd className={`h-4 w-4 transition-transform ${showDraftCreator ? 'rotate-45' : ''}`} />
                            </button>
                        </div>

                        {showDraftCreator && (
                            <div className="mb-2 ml-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Version name
                                </label>
                                <input
                                    ref={newDraftNameInputRef}
                                    type="text"
                                    value={newDraftName}
                                    onChange={(event) => setNewDraftName(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleCreateDraft();
                                        }
                                        if (event.key === 'Escape') {
                                            event.preventDefault();
                                            setShowDraftCreator(false);
                                            setNewDraftName('');
                                        }
                                    }}
                                    placeholder="Enter version name..."
                                    disabled={creatingDraft}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                />
                                <div className="mt-2.5 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCreateDraft}
                                        disabled={creatingDraft || !newDraftName.trim()}
                                        className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {creatingDraft ? 'Creating...' : 'Create'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowDraftCreator(false);
                                            setNewDraftName('');
                                        }}
                                        disabled={creatingDraft}
                                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {!currentDraftInFilter && currentDraftName ? (
                            <div className="mb-2 ml-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                Current draft is scoped to another period.
                            </div>
                        ) : null}

                        <div className="relative ml-1 space-y-1.5 border-l border-gray-200 pl-3">
                            {loadingDrafts && filteredDrafts.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-gray-200 bg-white/70 px-3 py-5 text-center text-sm text-gray-400">
                                    Loading versions…
                                </div>
                            ) : filteredDrafts.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-gray-200 bg-white/70 px-3 py-5 text-center text-sm text-gray-400">
                                    No versions yet
                                </div>
                            ) : (
                                filteredDrafts.map((draft) => {
                                    const isActive = isDraftActiveForPeriod(draft);
                                    const isSelected = draft.draft_name === currentDraftName;

                                    return (
                                        <div
                                            key={draft.draft_name}
                                            className={`relative rounded-xl border transition-all ${isActive
                                                ? 'border-green-300 bg-green-50 shadow-sm'
                                                : isSelected
                                                    ? 'border-blue-300 bg-blue-50 shadow-sm'
                                                    : 'border-gray-200/80 bg-white hover:border-gray-300 hover:bg-gray-50'
                                                }`}
                                            title={isActive ? `${draft.draft_name} is live for this period` : draft.draft_name}
                                        >
                                            <span
                                                className="absolute -left-[0.8125rem] top-1/2 h-px w-3 -translate-y-1/2 bg-gray-200"
                                                aria-hidden="true"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleLoadDraft(draft.draft_name)}
                                                className="w-full px-3 py-2.5 pr-10 text-left"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className={`h-2 w-2 flex-none rounded-full ${isActive ? 'bg-green-500 animate-pulse' : isSelected ? 'bg-blue-500' : 'bg-gray-300'}`} />
                                                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                                                        {draft.draft_name}
                                                    </span>
                                                    {isActive && (
                                                        <span className="flex-none rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                                                            Live
                                                        </span>
                                                    )}
                                                </div>
                                                {isSelected && (
                                                    <p className="mt-1 pl-4 text-xs font-medium text-blue-700">
                                                        Currently open
                                                    </p>
                                                )}
                                            </button>

                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setOpenMenuDraftName((openDraftName) => (
                                                        openDraftName === draft.draft_name ? null : draft.draft_name
                                                    ));
                                                }}
                                                className="absolute right-1.5 top-1.5 rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-gray-700 hover:shadow-sm"
                                                aria-label={`Open actions for ${draft.draft_name}`}
                                                title="Draft actions"
                                            >
                                                <IoEllipsisHorizontal className="h-4 w-4" />
                                            </button>

                                            {openMenuDraftName === draft.draft_name && (
                                                <>
                                                    <div className="absolute right-1.5 top-9 z-50 w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleLiveDraft(draft, isActive)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            {isActive ? 'Remove from Live' : 'Set Live'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => openMakePeriodDialog(draft)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Make new period...
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDuplicateDraft(draft)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Duplicate
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRenameDraft(draft)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Rename
                                                        </button>
                                                        <div className="my-1 border-t border-gray-100" />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteDraft(draft, isActive)}
                                                            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="fixed inset-0 z-40 cursor-default"
                                                        aria-label="Close draft actions"
                                                        onClick={() => setOpenMenuDraftName(null)}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <MakePeriodFromDraftModal
                isOpen={makePeriodDraft != null}
                onClose={() => setMakePeriodDraft(null)}
                draft={makePeriodDraft}
                sourcePeriod={makePeriodSourcePeriod}
                livePeriodName={makePeriodLivePeriodName}
                onSuccess={handlePeriodCreatedFromDraft}
                setError={setError}
            />
        </aside>
    );
};

export default DraftPeriodSidebar;
